import { PageHeader } from "@bednbody/ui/components/page-header";
import { NativeSelectOption } from "@bednbody/ui/components/native-select";
import { NativeSelect } from "@bednbody/ui/components/native-select";
import { Callout } from "@bednbody/ui/components/callout";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@bednbody/ui/components/card";
import { Checkbox } from "@bednbody/ui/components/checkbox";
import { Input } from "@bednbody/ui/components/input";
import { Label } from "@bednbody/ui/components/label";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { Textarea } from "@bednbody/ui/components/textarea";
import type { ScheduleEntryInput, StaffScheduleEntry } from "@bednbody/types";
import {
  ArrowClockwiseIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CheckCircleIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { EdgeFunctionError } from "@/lib/edge-functions";
import { useAuth } from "@/lib/auth";
import { ScheduleCellDialog } from "./components/ScheduleCellDialog";
import { WeeklyScheduleGrid } from "./components/WeeklyScheduleGrid";
import {
  currentManilaWeekStart,
  dutyInput,
  formatWeekRange,
  shiftWeek,
  weekDates,
  WEEKDAY_LABELS,
} from "./schedule-display";
import {
  type ScheduleStaff,
  useApplySchedulePattern,
  useCreateScheduleDraft,
  usePublishSchedule,
  useRemoveScheduleEntry,
  useScheduleStaff,
  useScheduleWeek,
  useUpsertScheduleEntry,
  useValidateScheduleDraft,
} from "./schedules.hooks";

interface EditingCell {
  staff: ScheduleStaff;
  date: string;
  entry?: StaffScheduleEntry;
}

export function SchedulesPage() {
  const { claims } = useAuth();
  const hubId = claims?.hub_id ?? "";
  const [weekStart, setWeekStart] = useState(currentManilaWeekStart);
  const dates = useMemo(() => weekDates(weekStart), [weekStart]);
  const staffQuery = useScheduleStaff(hubId);
  const weekQuery = useScheduleWeek(hubId, weekStart);
  const versions = weekQuery.data ?? [];
  const draft = versions.find((version) => version.status === "draft");
  const published = versions.find((version) => version.status === "published");
  const displayed = draft ?? published;

  const createDraft = useCreateScheduleDraft();
  const upsertEntry = useUpsertScheduleEntry();
  const removeEntry = useRemoveScheduleEntry();
  const applyPattern = useApplySchedulePattern();
  const validateDraft = useValidateScheduleDraft();
  const publishSchedule = usePublishSchedule();

  const [editing, setEditing] = useState<EditingCell | null>(null);
  const [selectedStaffIds, setSelectedStaffIds] = useState<Set<string>>(new Set());
  const [selectedDays, setSelectedDays] = useState<Set<number>>(new Set([0, 1, 2, 3, 4]));
  const [bulkKind, setBulkKind] = useState<"duty" | "rest_day">("duty");
  const [bulkStart, setBulkStart] = useState("");
  const [bulkEnd, setBulkEnd] = useState("");
  const [draftReason, setDraftReason] = useState("");
  const [publishReason, setPublishReason] = useState("");

  if (!hubId) {
    return (
      <Callout title="Weekly Schedules" tone="danger">
        Your verified account has no hub assignment. Schedule access is unavailable.
      </Callout>
    );
  }

  const mutationError = [
    createDraft,
    upsertEntry,
    removeEntry,
    applyPattern,
    validateDraft,
    publishSchedule,
  ].find((mutation) => mutation.isError)?.error;
  const isStale =
    mutationError instanceof EdgeFunctionError && mutationError.code === "STALE_VERSION";
  const isWriting =
    createDraft.isPending ||
    upsertEntry.isPending ||
    removeEntry.isPending ||
    applyPattern.isPending ||
    publishSchedule.isPending;

  const createFromCurrent = () => {
    if (!draftReason.trim()) return;
    createDraft.mutate({
      weekStart,
      changeReason: draftReason.trim(),
      ...(published ? { sourceVersionId: published.id } : {}),
      ...(published
        ? {
            entries: published.entries.map((entry) => ({
              staffId: entry.staffId,
              entry:
                entry.kind === "rest_day"
                  ? {
                      kind: "rest_day" as const,
                      scheduleDate: entry.scheduleDate,
                      ...(entry.availabilityNote
                        ? { availabilityNote: entry.availabilityNote }
                        : {}),
                    }
                  : {
                      kind: "duty" as const,
                      scheduleDate: entry.scheduleDate,
                      dutyStart: entry.dutyStart!,
                      dutyEnd: entry.dutyEnd!,
                      ...(entry.availabilityNote
                        ? { availabilityNote: entry.availabilityNote }
                        : {}),
                    },
            })),
          }
        : {}),
    });
  };

  const saveCell = (entry: ScheduleEntryInput) => {
    if (!draft || !editing) return;
    upsertEntry.mutate(
      {
        scheduleVersionId: draft.id,
        expectedUpdatedAt: draft.updatedAt,
        staffId: editing.staff.id,
        entry,
      },
      { onSuccess: () => setEditing(null) },
    );
  };

  const clearCell = () => {
    if (!draft || !editing?.entry) return;
    removeEntry.mutate(
      {
        scheduleVersionId: draft.id,
        expectedUpdatedAt: draft.updatedAt,
        entryId: editing.entry.id,
      },
      { onSuccess: () => setEditing(null) },
    );
  };

  const applyBulk = () => {
    if (!draft || selectedStaffIds.size === 0 || selectedDays.size === 0) return;
    const entries = [...selectedDays]
      .sort()
      .map((index) =>
        bulkKind === "rest_day"
          ? ({ kind: "rest_day", scheduleDate: dates[index]! } as const)
          : dutyInput(dates[index]!, bulkStart, bulkEnd),
      );
    applyPattern.mutate({
      scheduleVersionId: draft.id,
      expectedUpdatedAt: draft.updatedAt,
      staffIds: [...selectedStaffIds],
      entries,
    });
  };

  return (
    <div className="min-w-0 space-y-8">
      <PageHeader
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              aria-label="Previous week"
              onClick={() => setWeekStart((value) => shiftWeek(value, -1))}
            >
              <CaretLeftIcon />
            </Button>
            <div className="min-w-40 text-center text-sm font-medium">
              {formatWeekRange(weekStart)}
            </div>
            <Button
              variant="outline"
              size="icon"
              aria-label="Next week"
              onClick={() => setWeekStart((value) => shiftWeek(value, 1))}
            >
              <CaretRightIcon />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Refresh schedule"
              onClick={() => weekQuery.refetch()}
            >
              <ArrowClockwiseIcon />
            </Button>
          </div>
        }
        description="Therapists and riders · own-hub schedule management"
        title="Weekly Schedules"
      />

      {mutationError instanceof Error ? (
        <div className="flex items-start justify-between gap-3 rounded-xl bg-destructive/10 p-3 text-sm text-destructive">
          <span>
            {isStale ? "This draft changed. Refresh before editing again." : mutationError.message}
          </span>
          {isStale ? (
            <Button size="sm" variant="outline" onClick={() => weekQuery.refetch()}>
              Refresh
            </Button>
          ) : null}
        </div>
      ) : null}

      {weekQuery.isLoading || staffQuery.isLoading ? (
        <Skeleton className="h-96 w-full rounded-xl" />
      ) : (
        <>
          <Card size="sm">
            <CardContent className="flex min-w-0 flex-wrap items-end justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Week status</span>
                  <Badge variant={draft ? "warning" : published ? "default" : "outline"}>
                    {draft
                      ? `Draft v${draft.versionNumber}`
                      : published
                        ? `Published v${published.versionNumber}`
                        : "Not started"}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Changes affect capacity only after a conflict-free publication.
                </p>
              </div>
              {!draft ? (
                <div className="flex min-w-0 flex-1 flex-wrap items-end justify-end gap-2">
                  <div className="w-full max-w-sm space-y-1.5">
                    <Label htmlFor="draft-reason">Draft change reason</Label>
                    <Input
                      id="draft-reason"
                      maxLength={500}
                      value={draftReason}
                      onChange={(event) => setDraftReason(event.target.value)}
                      placeholder={
                        published
                          ? "Why is this schedule being revised?"
                          : "Why is this schedule being created?"
                      }
                    />
                  </div>
                  <Button
                    disabled={!draftReason.trim() || createDraft.isPending}
                    onClick={createFromCurrent}
                  >
                    {published ? "Create revision" : "Create draft"}
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <WeeklyScheduleGrid
            staff={staffQuery.data ?? []}
            dates={dates}
            entries={displayed?.entries ?? []}
            editable={Boolean(draft)}
            selectedStaffIds={selectedStaffIds}
            onSelectStaff={(staffId, selected) =>
              setSelectedStaffIds((current) => {
                const next = new Set(current);
                if (selected) next.add(staffId);
                else next.delete(staffId);
                return next;
              })
            }
            onEdit={(staff, date, entry) => setEditing({ staff, date, entry })}
          />

          {draft ? (
            <div className="grid gap-4 xl:grid-cols-[1fr_360px]">
              <Card size="sm">
                <CardHeader>
                  <CardTitle>
                    <h2>Bulk weekly pattern</h2>
                  </CardTitle>
                  <CardDescription>
                    Select staff in the grid, choose days, then apply Duty or Rest Day.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex min-w-0 flex-col gap-4">
                  <div className="flex flex-wrap gap-3">
                    {WEEKDAY_LABELS.map((label, index) => (
                      <label key={label} className="inline-flex items-center gap-1.5 text-sm">
                        <Checkbox
                          checked={selectedDays.has(index)}
                          onCheckedChange={(checked) =>
                            setSelectedDays((current) => {
                              const next = new Set(current);
                              if (checked === true) next.add(index);
                              else next.delete(index);
                              return next;
                            })
                          }
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-end gap-3">
                    <label className="space-y-1 text-sm">
                      <span className="block font-medium">Pattern</span>
                      <NativeSelect
                        className="h-9"
                        value={bulkKind}
                        onChange={(event) => setBulkKind(event.target.value as "duty" | "rest_day")}
                      >
                        <NativeSelectOption value="duty">Duty</NativeSelectOption>
                        <NativeSelectOption value="rest_day">Rest Day</NativeSelectOption>
                      </NativeSelect>
                    </label>
                    {bulkKind === "duty" ? (
                      <>
                        <label className="space-y-1 text-sm">
                          <span className="block font-medium">Start</span>
                          <Input
                            type="time"
                            value={bulkStart}
                            onChange={(event) => setBulkStart(event.target.value)}
                          />
                        </label>
                        <label className="space-y-1 text-sm">
                          <span className="block font-medium">End</span>
                          <Input
                            type="time"
                            value={bulkEnd}
                            onChange={(event) => setBulkEnd(event.target.value)}
                          />
                        </label>
                      </>
                    ) : null}
                    <Button
                      disabled={
                        selectedStaffIds.size === 0 ||
                        selectedDays.size === 0 ||
                        (bulkKind === "duty" && (!bulkStart || !bulkEnd)) ||
                        applyPattern.isPending
                      }
                      onClick={applyBulk}
                    >
                      Apply to {selectedStaffIds.size} staff
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card size="sm">
                <CardHeader>
                  <CardTitle>
                    <h2>Validate and publish</h2>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex min-w-0 flex-col gap-3">
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled={validateDraft.isPending || isWriting}
                    onClick={() =>
                      validateDraft.mutate({
                        scheduleVersionId: draft.id,
                        expectedUpdatedAt: draft.updatedAt,
                      })
                    }
                  >
                    Validate draft
                  </Button>
                  {validateDraft.data ? <ValidationSummary result={validateDraft.data} /> : null}
                  <Label htmlFor="publish-reason">Publication reason</Label>
                  <Textarea
                    id="publish-reason"
                    maxLength={500}
                    value={publishReason}
                    onChange={(event) => setPublishReason(event.target.value)}
                    placeholder="Record why this version is ready"
                  />
                  <Button
                    className="w-full"
                    disabled={!publishReason.trim() || publishSchedule.isPending || isWriting}
                    onClick={() =>
                      publishSchedule.mutate({
                        scheduleVersionId: draft.id,
                        expectedUpdatedAt: draft.updatedAt,
                        changeReason: publishReason.trim(),
                      })
                    }
                  >
                    Publish schedule
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Conflicts block publication. This release has no override action.
                  </p>
                </CardContent>
              </Card>
            </div>
          ) : null}

          <Card size="sm">
            <CardHeader>
              <CardTitle>
                <h2>Version history</h2>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {versions.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">No versions for this week.</p>
              ) : (
                <ol className="mt-3 divide-y">
                  {versions.map((version) => (
                    <li
                      key={version.id}
                      className="flex flex-wrap justify-between gap-2 py-2 text-sm"
                    >
                      <span>
                        v{version.versionNumber} · {version.changeReason}
                      </span>
                      <span className="text-muted-foreground">
                        {version.publishedAt
                          ? `Published ${new Date(version.publishedAt).toLocaleString()}`
                          : `Draft updated ${new Date(version.updatedAt).toLocaleString()}`}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <ScheduleCellDialog
        open={Boolean(editing)}
        staff={editing?.staff ?? null}
        scheduleDate={editing?.date ?? null}
        entry={editing?.entry}
        pending={upsertEntry.isPending || removeEntry.isPending}
        onOpenChange={(open) => !open && setEditing(null)}
        onSave={saveCell}
        onClear={clearCell}
      />
    </div>
  );
}

function ValidationSummary({
  result,
}: {
  result: {
    valid: boolean;
    blockers: Array<{ code: string; staffId?: string; scheduleDate?: string }>;
  };
}) {
  if (result.valid) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-primary/10 p-3 text-sm text-primary-dark">
        <CheckCircleIcon weight="fill" /> No blocking conflicts found.
      </div>
    );
  }
  return (
    <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
      <div className="mb-2 flex items-center gap-2 font-medium">
        <WarningCircleIcon weight="fill" /> {result.blockers.length} blocking conflict(s)
      </div>
      <ul className="space-y-1 text-xs">
        {result.blockers.map((blocker, index) => (
          <li key={`${blocker.code}-${blocker.staffId ?? "none"}-${index}`}>
            {blocker.code.replaceAll("_", " ")}
            {blocker.scheduleDate ? ` · ${blocker.scheduleDate}` : ""}
            {blocker.staffId ? ` · staff ${blocker.staffId.slice(0, 8)}` : ""}
          </li>
        ))}
      </ul>
    </div>
  );
}
